import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { FormField } from '../../components/Formulaire/FormField';
import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { chargerCapacites, CAPACITES_PAR_DEFAUT } from '../../components/Config/capacites';
import { aOffreRh } from '../../components/Auth/permissions';
import { notify } from '../../components/Utils/Notifications';
import CreatableSelect from 'react-select/creatable';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import PhoneInput from '../../components/Utils/PhoneInput';
import { FiAlertTriangle, FiSend } from 'react-icons/fi';


/**
 * `modele` : création par duplication (YRH-SHARED-010-T2, Yros/Kavero#195). Le
 * serveur a dit ce qui se copie de l'employé source (manager, département,
 * poste, compte Portail) ; le formulaire ne reprend que cela, laisse tout le
 * reste à saisir, et envoie `dupliqueDe` pour que le serveur laisse sa trace.
 * Les champs copiés restent modifiables. `onChangerSource` et `onPartirDeZero`
 * sont rendus au parent, qui tient le choix de la source.
 */
const EmployeeForm = ({ refreshEmployee, onSuccess, modele = null, onChangerSource, onPartirDeZero }) => {
    const { t } = useTranslation();
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor,
            boxShadow: state.isFocused ? '0 0 0 1px var(--color-primary)' : provided.boxShadow,
            '&:hover': {
                borderColor: state.isFocused ? 'var(--color-primary)' : provided.borderColor
            },
            borderRadius: '0.375rem',
            padding: '2px',
            minHeight: '42px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--color-primary)'
                : state.isFocused
                    ? 'rgba(var(--color-primary-rgb), 0.1)'
                    : provided.backgroundColor,
            color: state.isSelected ? 'white' : provided.color
        })
    };
    const { id } = useParams();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [departmentsOptions, setDepartmentsOptions] = useState([]);
    // Lieu de travail (contrat C16, Yros/Kavero#381). Il porte la zone que le
    // contrôle de pointage compare : sans affectation, tout pointage est
    // `non_verifiable`, donc refusé en mode strict. Les options viennent de
    // C13 — les lieux que la RH a configurés dans Paramètres → Pointage — et
    // non d'une liste saisie ici : une référence, jamais du texte libre.
    const [lieuxTravailOptions, setLieuxTravailOptions] = useState([]);
    const [lieuTravail, setLieuTravail] = useState(null);
    // Valeur telle qu'elle est en base. La comparer avant d'écrire évite un
    // appel — et un échec possible — quand la RH n'a pas touché au champ.
    const [lieuTravailInitial, setLieuTravailInitial] = useState(null);
    const [assignmentsOptions, setAssignmentsOptions] = useState([]);
    const [employee, setEmployee] = useState({
        name: '',
        matricule: '',
        gender: 'male',
        birthday: '',
        email: '',
        poste: '',
        manager: '',
        manager_name: '',
        phone: '',
        address: '',
        maritalStatus: '',
        spouseIncome: false,
        numberOfChildren: 0,
        nationality: '',
        idCardOrPassport: '',
        ssnid: '',
        company_country_code: '',
        place_of_birth: '',
        bank_name: '',
        account_number: '',
        is_non_resident: false,
        is_civil_servant: false,
        categories: [],
        department: null,
        department_name: '',
        assignment: null,
        assignment_name: '',
        portalAccount: false
    });

    // Capacités annoncées par l'instance (contrat C2). L'écran de création
    // d'employé est commun à YROS RH et YROS Paie ; seule l'option « compte
    // Portail » est propre au RH, et elle ne s'affiche que si l'instance la
    // déclare. Module RH absent → route absente → case masquée.
    const [capacites, setCapacites] = useState(CAPACITES_PAR_DEFAUT);

    useEffect(() => {
        let vivant = true;
        chargerCapacites().then(c => { if (vivant) setCapacites(c); });
        return () => { vivant = false; };
    }, []);

    // Duplication : seuls les champs copiés sont écrits, ce que la RH a déjà
    // saisi (nom, e-mail, matricule…) reste. Une autre source les remplace.
    useEffect(() => {
        if (!modele) return;
        setEmployee(prev => ({
            ...prev,
            poste: modele.poste || '',
            manager: modele.manager ? modele.manager.id : '',
            manager_name: modele.manager ? modele.manager.nom : '',
            department: modele.departement ? modele.departement.id : null,
            department_name: modele.departement ? modele.departement.nom : '',
        }));
    }, [modele]);

    // La case suit l'usage de la source, si l'instance propose l'option.
    useEffect(() => {
        if (!modele) return;
        setEmployee(prev => ({
            ...prev,
            portalAccount: Boolean(modele.compte_portail && capacites.compte_portail),
        }));
    }, [modele, capacites.compte_portail]);

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(response => {
                const options = response.data.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }));
                setCategoriesOptions(options);
            })
            .catch(error => {
                console.error('Erreur lors du chargement des catégories', error);
            });

        // Lieux de travail de la société active, via C13. Un échec ne casse
        // pas le formulaire : la liste reste vide et le reste de la fiche
        // s'enregistre — le lieu est une capacité RH, pas un champ vital.
        // Une société Paie seule n'a pas le RH : rien à lire (YRH-FOUND-014).
        if (aOffreRh()) {
            apiService.getConfigurationPointage()
                .then(reponse => {
                    setLieuxTravailOptions(
                        (reponse?.lieux || []).map(lieu => ({ value: lieu.id, label: lieu.nom }))
                    );
                })
                .catch(() => setLieuxTravailOptions([]));
        }

        // load departments
        apiService.getElement('/employee/departments')
            .then(response => {
                const opts = response.data.departments.map(d => ({ value: d.id, label: d.name }));
                setDepartmentsOptions(opts);
            })
            .catch(err => {
                console.error('Erreur chargement départements', err);
            });

        // load assignments
        apiService.getElement('/employee/assignments')
            .then(response => {
                const opts = response.data.assignments.map(a => ({ value: a.id, label: a.name }));
                setAssignmentsOptions(opts);
            })
            .catch(err => {
                console.error('Erreur chargement assignments', err);
            });
    }, []);

    useEffect(() => {
        if (id) {
            setErrorMessage('');
            setLoading(true);
            apiService.getElementById('/employee', id).then(response => {
                const employeeData = response.data;
                console.log('Employee data:', employeeData);

                // map potential server fields to local state keys
                const mapped = {
                    ...employee,
                    ...employeeData,
                    numberOfChildren: Math.max(0, parseInt(employeeData.numberOfChildren, 10) || 0),
                };
                // handle department coming from API as department_id or department
                if (employeeData.department_id) {
                    mapped.department = employeeData.department_id.id || employeeData.department_id;
                    mapped.department_name = employeeData.department_id.name || mapped.department_name;
                }
                if (employeeData.assignment_id) {
                    mapped.assignment = employeeData.assignment_id.id || employeeData.assignment_id;
                    mapped.assignment_name = employeeData.assignment_id.name || mapped.assignment_name;
                }
                // categories may come as category_ids array
                if (employeeData.category_ids && Array.isArray(employeeData.category_ids)) {
                    mapped.categories = employeeData.category_ids.map(c => (typeof c === 'object' ? { id: c.id, name: c.name } : { id: c, name: '' }));
                }

                setEmployee(mapped);

                // Affectation courante : elle ne vient pas du GET de la fiche
                // — celui-ci est servi par `salaire_pro`, absent d'une
                // instance RH pure — mais de la route RH dédiée.
                if (aOffreRh()) {
                    apiService.getLieuTravail(id)
                        .then(reponse => {
                            const actuel = reponse?.lieu_id ?? null;
                            setLieuTravail(actuel);
                            setLieuTravailInitial(actuel);
                        })
                        .catch(() => {});
                }

            }).catch(error => {
                console.error('Error fetching employee:', error);
                notify(t('employeeForm.fetchError'), '', 3);

            }).finally(() => setLoading(false));
        }
    }, [id]);

    const handleChange = (eOrValue, fieldName) => {
        const nextName = fieldName || eOrValue?.target?.name;
        const nextValue = fieldName ? eOrValue : eOrValue?.target?.value;

        if (!nextName) {
            return;
        }

        setEmployee({
            ...employee,
            [nextName]: nextValue
        });
    };

    const handleSelectChange = (selectedOption) => {
        setEmployee({
            ...employee,
            nationality: selectedOption.value
        });
    };

    const handleManagerChange = (selectedOption) => {
        setEmployee({
            ...employee,
            manager: selectedOption ? selectedOption.value : null,
            manager_name: selectedOption ? selectedOption.label : ''
        });
    };

    const handleDepartmentChange = (selectedOption) => {
        setEmployee({
            ...employee,
            department: selectedOption ? selectedOption.value : null,
            department_name: selectedOption ? selectedOption.label : ''
        });
    };

    const handleAssignmentChange = (selectedOption) => {
        setEmployee({
            ...employee,
            assignment: selectedOption ? selectedOption.value : null,
            assignment_name: selectedOption ? selectedOption.label : ''
        });
    };

    /**
     * Enregistre l'affectation, si elle a changé. Chemin séparé de la fiche :
     * elle passe par une route RH, la fiche par une route Paie. Rend `false`
     * en cas d'échec pour que l'appelant le dise — la fiche, elle, est déjà
     * enregistrée à ce stade, et laisser croire le contraire serait pire que
     * l'échec.
     */
    const enregistrerLieuTravail = async (employeId) => {
        if (lieuTravail === lieuTravailInitial) return true;
        try {
            await apiService.affecterLieuTravail(employeId, lieuTravail);
            setLieuTravailInitial(lieuTravail);
            return true;
        } catch (erreur) {
            signalerErreur(erreur, 'EmployeeForm.enregistrerLieuTravail');
            notify(t('employeeForm.workLocationError'), '', 3);
            return false;
        }
    };

    // Un refus du serveur arrive en HTTP 200, dans `result.error` : axios
    // ne passe pas par le `.catch`. Le message est écrit pour l'utilisateur
    // (Yros/Kavero#312) ; le texte générique, qui invite à réessayer, ne sert
    // que quand il n'y en a pas (Yros/Kavero#467).
    const signalerRefus = (response, cleGenerique) => {
        const recu = response?.data?.result?.error;
        const message = typeof recu === 'string' && recu ? recu : t(cleGenerique);
        setErrorMessage(message);
        notify(message, '', 3);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        const payload = {
            ...employee,
            numberOfChildren: Math.max(0, parseInt(employee.numberOfChildren, 10) || 0),
            category_ids: (employee.categories || []).map(cat => cat.id),
            department_id: employee.department || false,
            assignment_id: employee.assignment || false
        };
        if (id) {
            apiService
                .updateElementById("/employee", id, payload)
                .then(async response => {
                    if(response.data?.result?.employee_id) {
                        // L'affectation part après la fiche : si elle échoue,
                        // la fiche reste enregistrée et c'est ce que le
                        // message doit dire.
                        const lieuOk = await enregistrerLieuTravail(Number(id));
                        if (lieuOk) {
                            notify(t('employeeForm.updateSuccess'), '', 2);
                        }
                        refreshEmployee();
                        onSuccess(id);
                    } else {
                        signalerRefus(response, 'employeeForm.updateError');
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    setErrorMessage(t('employeeForm.updateError'));
                    notify(t('employeeForm.updateError'), '', 3);
                    setLoading(false);
                });
        } else {
            apiService
                .saveElement('/employee', modele ? { ...payload, dupliqueDe: modele.source.id } : payload)
                .then(async response => {
                    // Check for error_type in response
                    console.log('Create employee response:', response);
                    if (response.data?.result.error_type === 'limit_exceeded') {
                        setErrorMessage(t('employeeForm.limitExceededDetail'));
                        notify(t('employeeForm.limitExceeded'), '', 3);
                        setLoading(false);
                        return;
                    }
                    const newEmployeeId = response?.data?.employee_id || response?.data?.result?.employee_id;
                    if (newEmployeeId) {
                        // À la création, l'identifiant n'existe qu'ici :
                        // l'affectation ne peut pas partir avant.
                        const lieuOk = await enregistrerLieuTravail(Number(newEmployeeId));
                        if (lieuOk) {
                            notify(t('employeeForm.createSuccess'), '', 2);
                        }
                        onSuccess(newEmployeeId);
                    } else {
                        signalerRefus(response, 'employeeForm.createError');
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    setErrorMessage(t('employeeForm.createError'));
                    setLoading(false);
                });
        }
    };

    // Borne haute de la date de naissance : aujourd'hui, en heure locale.
    // toISOString() donnerait la date UTC, décalée d'un jour en fin de journée.
    const now = new Date();
    const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const options = countryList().getData();
    const companyInfoStr = localStorage.getItem('companyInfo');
    const companyInfo = companyInfoStr ? JSON.parse(companyInfoStr) : null;
    const isCoteDIvoire = employee.company_country_code === 'CI' || companyInfo?.country_code === 'CI';
    const isNiger = employee.company_country_code === 'NE' || companyInfo?.country_code === 'NE';

    // Option propre à YROS RH, proposée à la création seulement (voir le
    // commentaire sur l'encadré).
    const proposerComptePortail = Boolean(capacites.compte_portail && !id);

    // Duplication : une pastille « copié » tant que le champ porte encore la
    // valeur reprise de la source.
    const copies = {
        poste: Boolean(modele?.poste) && employee.poste === modele.poste,
        manager: Boolean(modele?.manager) && employee.manager === modele.manager.id,
        departement: Boolean(modele?.departement) && employee.department === modele.departement.id,
    };
    const pastilleCopie = (cle) => copies[cle] && (
        <span
            data-testid={`duplication-copie-${cle}`}
            className="ml-2 rounded-full bg-[--color-primary]/10 px-2 py-0.5 text-xs font-semibold text-[--color-primary]"
        >
            {t('employeeForm.duplication.copie')}
        </span>
    );
    // Repartir de zéro efface la saisie : on ne le fait pas sans le dire.
    const saisieFaite = [
        employee.name, employee.email, employee.matricule, employee.phone, employee.birthday,
        employee.address, employee.idCardOrPassport, employee.ssnid, employee.bank_name,
        employee.account_number, employee.place_of_birth,
    ].some(valeur => Boolean(valeur));
    const partirDeZero = () => {
        if (saisieFaite && !window.confirm(t('employeeForm.duplication.confirmerDeZero'))) return;
        if (onPartirDeZero) onPartirDeZero();
    };

    return (
        <>

            <form onSubmit={handleSubmit} className="p-6">
                {modele && (
                    <div
                        data-testid="duplication-bandeau"
                        className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-[--color-primary] bg-[--color-primary]/5 px-4 py-3"
                    >
                        <div className="flex-1 min-w-[16rem]">
                            <div className="font-semibold">
                                {t('employeeForm.duplication.copieDepuis')}{' '}
                                <strong data-testid="duplication-source">{modele.source.nom}</strong>
                            </div>
                            <div className="text-sm text-gray-600">{t('employeeForm.duplication.copieAide')}</div>
                        </div>
                        <button
                            type="button"
                            data-testid="duplication-changer"
                            onClick={() => onChangerSource && onChangerSource()}
                            className="rounded-lg border border-[--color-primary] bg-white px-3 py-1.5 text-sm font-medium text-[--color-primary]"
                        >
                            {t('employeeForm.duplication.changer')}
                        </button>
                        <button
                            type="button"
                            data-testid="duplication-de-zero"
                            onClick={partirDeZero}
                            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700"
                        >
                            {t('employeeForm.duplication.deZero')}
                        </button>
                    </div>
                )}
                <h2 className="text-lg font-bold mb-4 flex items-center">
                    <span className="w-2 h-6 bg-blue-500 rounded-sm mr-2"></span>
                    {t('employeeForm.personalInfo')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <FormField label={t('employeeForm.fullName')}>
                        <input
                            type="text"
                            name="name"
                            value={employee.name || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            required
                        />
                    </FormField>

                    <FormField label={t('employeeForm.gender')}>
                        <select
                            name="gender"
                            value={employee.gender || 'male'}
                            onChange={handleChange}
                            className="select select-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                            required
                        >
                            <option value="male">{t('employeeForm.male')}</option>
                            <option value="female">{t('employeeForm.female')}</option>
                        </select>
                    </FormField>

                    <FormField label={t('employeeForm.birthDate')}>
                        <input
                            type="date"
                            name="birthday"
                            max={todayISO}
                            value={employee.birthday || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>

                    <FormField label={t('employeeForm.placeOfBirth')}>
                        <input
                            type="text"
                            name="place_of_birth"
                            value={employee.place_of_birth || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>

                    <FormField label={t('employeeForm.email')}>
                        <input
                            type="email"
                            name="email"
                            value={employee.email || ''}
                            onChange={handleChange}
                            // L'invitation part à cette adresse : sans elle, le
                            // serveur refuse de créer le compte Portail.
                            required={proposerComptePortail && employee.portalAccount}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label={t('employeeForm.phone')}>
                        <PhoneInput
                            name="phone"
                            value={employee.phone || ''}
                            onChange={(value) => handleChange({ target: { name: 'phone', value } })}
                        />
                    </FormField>
                    <FormField label={t('employeeForm.address')}>
                        <input
                            type="text"
                            name="address"
                            value={employee.address || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>
                    <FormField label={t('employeeForm.idCardOrPassport')}>
                        <input
                            type="text"
                            name="idCardOrPassport"
                            value={employee.idCardOrPassport || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>

                    {/* Yros/Kavero#488 : saisissable dans tous les pays ; la CNPS garde son libellé. */}
                    <FormField label={t(isCoteDIvoire ? 'employeeForm.cnpsNumber' : 'employeeForm.socialSecurityNumber')}>
                        <input
                            type="text"
                            name="ssnid"
                            value={employee.ssnid || ''}
                            onChange={handleChange}
                            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
                        />
                    </FormField>

                    <FormField label={t('employeeForm.nationality')}>
                        <Select
                            name="nationality"
                            value={options.find(option => option.value === employee.nationality) || ''}
                            onChange={handleSelectChange}
                            options={options}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder={t('employeeForm.selectCountry')}
                            styles={customSelectStyles}
                        />
                    </FormField>
                    <FormField label="">
                            <span className="text-sm text-gray-500">{t('employeeForm.nonResidentCheck')}</span>
                            <input
                                type="checkbox"
                                id="formIsNonResident"
                                name="is_non_resident"
                                checked={employee.is_non_resident}
                                onChange={(e) => setEmployee({ ...employee, is_non_resident: e.target.checked })}
                                className="checkbox"
                            />
                    </FormField>
                    {isNiger && (
                        <FormField label="">
                            <span className="text-sm text-gray-500">{t('employeeForm.civilServantCheck')}</span>
                            <input
                                type="checkbox"
                                id="formIsCivilServant"
                                name="is_civil_servant"
                                checked={!!employee.is_civil_servant}
                                onChange={(e) => setEmployee({ ...employee, is_civil_servant: e.target.checked })}
                                className="checkbox"
                            />
                        </FormField>
                    )}
                    {/* Proposée à la création seulement : à la modification, le
                        serveur ne rend pas ce champ, donc l'état vaudrait faux
                        et enregistrer décocherait un employé qui a déjà un
                        compte. Le rattachement d'un employé existant aura sa
                        propre tâche. */}
                    {proposerComptePortail && (
                        <div className={`md:col-span-2 rounded-lg border p-4 transition-colors ${
                            employee.portalAccount
                                ? 'border-[--color-primary] bg-white shadow-sm'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <div className="flex items-start gap-4">
                                <FiSend className="mt-1 h-5 w-5 shrink-0 text-[--color-primary]" aria-hidden="true" />
                                <div className="flex-1">
                                    <label htmlFor="formPortalAccount" className="font-semibold text-gray-800 cursor-pointer">
                                        {t('employeeForm.portalAccountTitle')}
                                    </label>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t('employeeForm.portalAccountHelp')}
                                    </p>
                                    {modele && (
                                        <p data-testid="duplication-portail-note" className="text-sm text-[--color-primary] mt-2">
                                            {t(modele.compte_portail
                                                ? 'employeeForm.duplication.portailCoche'
                                                : 'employeeForm.duplication.portailDecoche',
                                            { nom: modele.source.nom })}
                                        </p>
                                    )}
                                    {employee.portalAccount && (
                                        employee.email ? (
                                            <p className="text-sm text-gray-700 mt-2">
                                                <span>{t('employeeForm.portalAccountInvitationTo')}</span>{' '}
                                                <strong className="font-semibold text-gray-900">{employee.email}</strong>
                                            </p>
                                        ) : (
                                            <p className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                                                <FiAlertTriangle className="shrink-0" aria-hidden="true" />
                                                <span>{t('employeeForm.portalAccountEmailMissing')}</span>
                                            </p>
                                        )
                                    )}
                                </div>
                                <input
                                    type="checkbox"
                                    id="formPortalAccount"
                                    name="portalAccount"
                                    checked={employee.portalAccount}
                                    onChange={(e) => setEmployee({ ...employee, portalAccount: e.target.checked })}
                                    className="toggle mt-1 checked:bg-[--color-primary] checked:border-[--color-primary]"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <h2 className="text-lg font-bold mb-4 flex items-center">
                    <span className="w-2 h-6 bg-orange-500 rounded-sm mr-2"></span>
                    {t('employeeForm.familyInfo')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormField label={t('employeeForm.maritalStatus')}>
                        <select
                            id="formMaritalStatus"
                            name="maritalStatus"
                            value={employee.maritalStatus || 'single'}
                            onChange={(e) => setEmployee({ ...employee, maritalStatus: e.target.value })}
                            className="select select-bordered"
                            required>
                            <option value="single">{t('employeeForm.single')}</option>
                            <option value="married">{t('employeeForm.married')}</option>
                            <option value="widower">{t('employeeForm.widower')}</option>
                            <option value="divorced">{t('employeeForm.divorced')}</option>
                        </select>
                    </FormField>

                    {employee.maritalStatus === 'married' && (
                        <FormField label={t('employeeForm.spouseIncome')}>
                            <select
                                id="formSpouseIncome"
                                name="spouseIncome"
                                value={employee.spouseIncome === true ? "yes" : "no"}
                                onChange={(e) => setEmployee({ ...employee, spouseIncome: e.target.value === "yes" })}
                                className="select select-bordered"
                                required>
                                <option value="no">{t('employeeForm.no')}</option>
                                <option value="yes">{t('employeeForm.yes')}</option>
                            </select>
                        </FormField>
                    )}
                    <FormField label={t('employeeForm.numberOfChildren')}>
                        <input
                            type="number"
                            name="numberOfChildren"
                            min="0"
                            value={employee.numberOfChildren || 0}
                            onChange={(e) => handleChange({ target: { name: 'numberOfChildren', value: Math.max(0, parseInt(e.target.value, 10) || 0) } })}
                            className="input input-bordered"
                        />
                    </FormField>
                </div>

                 <h2 className="text-lg font-bold mb-4 flex items-center">
                    <span className="w-2 h-6 bg-green-500 rounded-sm mr-2"></span>
                    {t('employeeForm.bankDetails')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormField label={t('employeeForm.bank')}>
                        <input
                            type="text"
                            name="bank_name"
                            value={employee.bank_name || ''}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                    <FormField label={t('employeeForm.accountNumber')}>
                        <input
                            type="text"
                            name="account_number"
                            value={employee.account_number || ''}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                </div>

                <h2 className="text-lg font-bold mb-4">{t('employeeForm.companyInfo')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <FormField label={t('employeeForm.matricule')}>
                        <input
                            type="text"
                            name="matricule"
                            value={employee.matricule || ''}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                    <FormField label={<>{t('employeeForm.position')}{pastilleCopie('poste')}</>}>
                        <input
                            type="text"
                            name="poste"
                            value={employee.poste || ''}
                            onChange={handleChange}
                            className="input input-bordered"
                        />
                    </FormField>
                    <FormField label={<>{t('employeeForm.manager')}{pastilleCopie('manager')}</>}>
                        <EmployeeSelect
                            value={employee.manager && employee.manager_name ? { value: employee.manager, label: employee.manager_name } : null}
                            onChange={handleManagerChange}
                            placeholder={t('employeeForm.chooseManager')}
                            nameKey="label"
                            isClearable
                        />
                        {/* Le manager de la source n'est plus actif : le champ reste
                            vide, la RH en choisit un ou le laisse vide. */}
                        {modele?.manager_inactif && !employee.manager && (
                            <p
                                role="status"
                                data-testid="duplication-manager-inactif"
                                className="mt-2 flex items-start gap-1 text-sm text-amber-700"
                            >
                                <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
                                <span>{t('employeeForm.duplication.managerInactif')}</span>
                            </p>
                        )}
                    </FormField>

                    <FormField label={<>{t('employeeForm.department')}{pastilleCopie('departement')}</>}>
                        <CreatableSelect
                            value={departmentsOptions.find(opt => opt.value === employee.department) || null}
                            onChange={(opt) => handleDepartmentChange(opt)}
                            options={departmentsOptions}
                            styles={customSelectStyles}
                            placeholder={t('employeeForm.selectOrCreate')}
                            isClearable
                            onCreateOption={async (inputValue) => {
                                try {
                                    const response = await apiService.saveElement('/employee/department', { name: inputValue });
                                    const newId = response?.data?.result?.id || response?.data?.id;
                                    const newName = response?.data?.result?.name || response?.data?.name;
                                    if (!newId) throw new Error('Création du département a échoué');
                                    const newOpt = { value: newId, label: newName };
                                    setDepartmentsOptions(prev => [...prev, newOpt]);
                                    setEmployee(prev => ({ ...prev, department: newId, department_name: newName }));
                                } catch (err) {
                                    console.error('Erreur création département:', err);
                                    notify(t('employeeForm.departmentCreateError'), '', 3);
                                }
                            }}
                        />
                    </FormField>

                    {aOffreRh() && (
                        <FormField label={t('employeeForm.workLocation')}>
                            {/* `Select` et non `CreatableSelect` : créer un lieu
                                d'ici demanderait des coordonnées et un rayon, qui
                                vivent dans Paramètres → Pointage. Une référence,
                                jamais du texte libre — « Siège Dakar » saisi à
                                côté d'un lieu nommé « Siege Dakar » laisserait le
                                contrôle de zone sans repère, et le salarié serait
                                refusé sans que rien ne l'explique. */}
                            {/* `FormField` ne câble pas `htmlFor` — manque
                                préexistant, commun à tous les champs de ce
                                formulaire. Plutôt que de l'élargir ici, ce champ
                                porte son propre nom accessible : lisible par un
                                lecteur d'écran, et adressable en test.
                                `classNamePrefix` rend les classes déterministes,
                                là où react-select génère sinon des noms emotion
                                illisibles. */}
                            <Select
                                inputId="employee-work-location"
                                aria-label={t('employeeForm.workLocation')}
                                classNamePrefix="lieu-travail"
                                value={lieuxTravailOptions.find(opt => opt.value === lieuTravail) || null}
                                onChange={(opt) => setLieuTravail(opt ? opt.value : null)}
                                options={lieuxTravailOptions}
                                styles={customSelectStyles}
                                placeholder={t('employeeForm.workLocationPlaceholder')}
                                noOptionsMessage={() => t('employeeForm.workLocationAucun')}
                                isClearable
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {t('employeeForm.workLocationHint')}
                            </p>
                        </FormField>
                    )}

                    <FormField label={t('employeeForm.assignment')}>
                        <CreatableSelect
                            value={assignmentsOptions.find(opt => opt.value === employee.assignment) || null}
                            onChange={(opt) => handleAssignmentChange(opt)}
                            options={assignmentsOptions}
                            styles={customSelectStyles}
                            placeholder={t('employeeForm.selectOrCreate')}
                            isClearable
                            onCreateOption={async (inputValue) => {
                                try {
                                    const response = await apiService.saveElement('/employee/assignment', { name: inputValue });
                                    const newId = response?.data?.result?.id || response?.data?.id;
                                    const newName = response?.data?.result?.name || response?.data?.name;
                                    if (!newId) throw new Error('Création de l\'assignment a échoué');
                                    const newOpt = { value: newId, label: newName };
                                    setAssignmentsOptions(prev => [...prev, newOpt]);
                                    setEmployee(prev => ({ ...prev, assignment: newId, assignment_name: newName }));
                                } catch (err) {
                                    console.error('Erreur création assignment:', err);
                                    notify(t('employeeForm.assignmentCreateError'), '', 3);
                                }
                            }}
                        />
                    </FormField>
                </div>
                <FormField label={t('employeeForm.tags')}>
                    <CreatableSelect
                        name="categories"
                        value={categoriesOptions.filter(opt => (employee.categories || []).some(cat => cat.id === opt.value))}
                        onChange={selected =>
                            setEmployee({
                                ...employee,
                                categories: selected ? selected.map(opt => ({ id: opt.value, name: opt.label })) : []
                            })
                        }
                        onCreateOption={async (inputValue) => {
                            // Appel API pour créer la catégorie
                            try {
                                const response = await apiService.saveElement('/employee/category', { name: inputValue });
                                console.log('New category created:', response);
                                const newCat = { value: response.data.result.id, label: response.data.result.name };
                                setCategoriesOptions(prev => [...prev, newCat]);
                                setEmployee({
                                    ...employee,
                                    categories: [...(employee.categories || []), { id: newCat.value, name: newCat.label }]
                                });
                            } catch (err) {
                                signalerErreur(err, 'EmployeeForm.onCreateOption');
                                notify(t('employeeForm.categoryCreateError'), '', 3);
                            }
                        }}
                        options={categoriesOptions}
                        isMulti
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder={t('employeeForm.selectOrCreateTag')}
                        styles={customSelectStyles}
                    />
                </FormField>
                {/* Au-dessus du bouton, pas en tête de formulaire : c'est là
                    que l'œil se trouve au moment de l'envoi. */}
                {errorMessage && (
                    <div role="alert" className="text-center text-red-600 mt-8 p-3 bg-red-50 border border-red-200 rounded-lg">
                        {errorMessage}
                    </div>
                )}
                <div className="flex justify-center mt-8 gap-4">
                    <button type="submit" className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-3 font-medium transition-all duration-200 shadow-sm">
                        {id ? t('employeeForm.updateEmployee') : t('employeeForm.createEmployee')}
                    </button>
                    {loading && (
                        <div className="flex justify-center">
                            <div className="loader">
                                <span className="loading loading-bars loading-xl text-[--color-primary]"></span>
                            </div>
                        </div>
                    )}
                </div>
            </form>
            
        </>
    );
};

export default EmployeeForm;